import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import { calculateTimelineMessage } from '../../utils/collegeTimelineCalculator';
import StrategicResultsRenderer from './shared/StrategicResultsRenderer';

const SavedStrategicProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const { addCollege, addEssay, addTimelineItem } = useCollegeContext();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Integration state
  const [integrationStatus, setIntegrationStatus] = useState({
    colleges: {},
    essays: {},
    timeline: {}
  });
  const [isIntegrating, setIsIntegrating] = useState(false);

  useEffect(() => {
    loadSavedProfile();
  }, [id]);

  const loadSavedProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to load from assessment_responses table first (new approach)
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('assessment_responses')
        .select('id, formatted_insights, insights, formatted_results_html, created_at, user_id, results_permalink')
        .eq('results_permalink', id)
        .single();

      if (assessmentData) {
        // Prioritize interactive structured format over static HTML
        // Try formatted_insights JSONB first (interactive format)
        if (assessmentData.formatted_insights) {
          const insights = assessmentData.formatted_insights;
          
          const transformedProfile = {
            id: assessmentData.id,
            user_id: assessmentData.user_id,
            created_at: assessmentData.created_at,
            results_permalink: assessmentData.results_permalink || id,
            content: insights, // Pass the full insights object for structured rendering
            title: insights.title || 'Strategic Assessment Results',
            subtitle: insights.subtitle || 'Personalized college guidance based on your responses'
          };
          
          setProfile(transformedProfile);
          return;
        }
        
        // Try old insights JSONB (interactive format)
        if (assessmentData.insights) {
          const insights = assessmentData.insights;
          
          const transformedProfile = {
            id: assessmentData.id,
            user_id: assessmentData.user_id,
            created_at: assessmentData.created_at,
            results_permalink: assessmentData.results_permalink || id,
            content: insights, // Pass the full insights object for structured rendering
            title: insights.title || 'Strategic Assessment Results',
            subtitle: insights.subtitle || 'Personalized college guidance based on your responses'
          };
          
          setProfile(transformedProfile);
          return;
        }
        
        // Fallback to formatted HTML only if no interactive format is available
        if (assessmentData.formatted_results_html) {
          const transformedProfile = {
            id: assessmentData.id,
            user_id: assessmentData.user_id,
            created_at: assessmentData.created_at,
            results_permalink: assessmentData.results_permalink || id,
            formatted_html: assessmentData.formatted_results_html,
            title: 'Strategic Assessment Results',
            subtitle: 'Personalized college guidance based on your responses'
          };
          
          setProfile(transformedProfile);
          return;
        }
      }

      // Fallback: try the original strategic_profiles table
      const { data, error } = await supabase
        .from('strategic_profiles')
        .select('*')
        .eq('results_permalink', id)
        .single();

      if (error) {
        console.error('Error loading saved profile:', error);
        if (error.code === 'PGRST116' || error.code === '42P01') {
          setError('Strategic profile not found. This may be an assessment result that needs to be regenerated.');
        } else {
          setError('Failed to load strategic profile');
        }
        return;
      }

      setProfile(data);
    } catch (error) {
      console.error('Error loading saved profile:', error);
      setError('Failed to load strategic profile');
    } finally {
      setLoading(false);
    }
  };

  // Extract college name from text like "Northwestern: Co-op program..." -> "Northwestern"
  // Also handles object format with school, name, or other properties
  const parseCollegeName = (schoolData) => {
    // Handle object format
    if (typeof schoolData === 'object' && schoolData !== null) {
      return schoolData.school || schoolData.name || schoolData.collegeName || 
             schoolData.title || JSON.stringify(schoolData);
    }
    
    // Handle string format
    if (typeof schoolData === 'string') {
      const colonIndex = schoolData.indexOf(':');
      return colonIndex > 0 ? schoolData.substring(0, colonIndex).trim() : schoolData.trim();
    }
    
    // Fallback for any other type
    return String(schoolData);
  };

  const addCollegeToList = async (schoolText, category) => {
    if (!addCollege || isIntegrating) return;
    
    setIsIntegrating(true);
    
    try {
      const collegeName = parseCollegeName(schoolText);
      const collegeKey = `${category}-${collegeName}`;
      
      // Check if already added
      if (integrationStatus.colleges[collegeKey]) {
        console.log('College already added:', collegeName);
        return;
      }
      
      // Extract notes from the original data
      let notes = '';
      if (typeof schoolText === 'object' && schoolText !== null) {
        // For object format, use available description fields
        notes = schoolText.whyYouMatch || schoolText.competitiveEdge || 
               schoolText.description || schoolText.reasoning || '';
      } else if (typeof schoolText === 'string' && schoolText.includes(':')) {
        // For string format, extract text after colon
        notes = schoolText.split(':')[1].trim();
      }

      const collegeData = {
        name: collegeName,
        category: category,
        source: 'strategic_assessment',
        notes: notes,
        addToGlobalColleges: true // Ensure AI-suggested colleges are added to global repository
      };
      
      console.log('Adding college to list:', collegeData);
      const newCollege = await addCollege(collegeData);
      
      if (newCollege) {
        setIntegrationStatus(prev => ({
          ...prev,
          colleges: {
            ...prev.colleges,
            [collegeKey]: { added: true, id: newCollege.id, name: collegeName }
          }
        }));
      }
    } catch (error) {
      console.error('Error adding college:', error);
    } finally {
      setIsIntegrating(false);
    }
  };


  const addActionToTimeline = async (action) => {
    if (!addTimelineItem || isIntegrating) return;
    
    setIsIntegrating(true);
    
    try {
      const actionKey = `step-${action.step}`;
      
      // Check if already added
      if (integrationStatus.timeline[actionKey]) {
        console.log('Action already added:', action.title);
        return;
      }
      
      // Parse timeline text to actual dates
      const timelineData = {
        title: action.title,
        description: action.description,
        category: 'application_prep',
        source: 'strategic_assessment',
        timeline_text: action.timeline
      };
      
      console.log('Adding action to timeline:', timelineData);
      const newTimelineItem = await addTimelineItem(timelineData);
      
      if (newTimelineItem) {
        setIntegrationStatus(prev => ({
          ...prev,
          timeline: {
            ...prev.timeline,
            [actionKey]: { added: true, id: newTimelineItem.id, title: action.title }
          }
        }));
      }
    } catch (error) {
      console.error('Error adding action to timeline:', error);
    } finally {
      setIsIntegrating(false);
    }
  };

  // Render structured insights using the shared StrategicResultsRenderer
  const renderStructuredInsights = (insights) => {
    if (!insights || typeof insights !== 'object') return null;

    return (
      <StrategicResultsRenderer 
        insights={insights}
        currentUser={currentUser}
        studentGradeLevel={""}
        integrationStatus={integrationStatus}
        addCollegeToList={addCollegeToList}
        addActionToTimeline={addActionToTimeline}
        isIntegrating={isIntegrating}
        calculateTimelineMessage={calculateTimelineMessage}
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading strategic profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-red-200">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Profile Not Found</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/college')}
              className="bg-slate-900 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors"
            >
              Return to College Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-x-hidden" style={{
      background: 'linear-gradient(135deg, #fafaf9 0%, #f0fdfa 30%, #fef2f7 100%)',
      minHeight: 'calc(100vh - 200px)'
    }}>
      {/* Background Pattern */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-0 w-full h-full opacity-40">
          <div className="absolute top-1/4 left-1/6 w-96 h-96 bg-teal-100 rounded-full blur-3xl opacity-30"></div>
          <div className="absolute top-3/4 right-1/6 w-80 h-80 bg-pink-100 rounded-full blur-3xl opacity-30"></div>
        </div>
      </div>

      {/* Metadata Bar */}
      <div className="relative z-10 bg-white bg-opacity-90 backdrop-blur-sm border-b border-stone-200 px-4 py-2">
        <div className="flex items-center justify-end gap-4 text-sm text-slate-500">
          {profile?.created_at && (
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>Saved {new Date(profile.created_at).toLocaleDateString()}</span>
            </div>
          )}
          {profile?.student_name && (
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>{profile.student_name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-8 py-16 max-w-6xl relative z-10">
        {profile && typeof profile.content === 'object' ? (
          // Structured insights format - use shared renderer
          renderStructuredInsights(profile.content)
        ) : profile?.formatted_html ? (
          // Fallback: Original strategic_profiles HTML format
          <div className="bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-3xl p-8 shadow-xl">
            <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: profile.formatted_html }} />
          </div>
        ) : profile?.content && typeof profile.content === 'string' ? (
          // Fallback: String content
          <div className="bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-3xl p-8 shadow-xl">
            <div className="prose prose-slate max-w-none">
              <pre className="whitespace-pre-wrap text-slate-700 leading-relaxed">{profile.content}</pre>
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white bg-opacity-95 backdrop-blur-xl border border-stone-200 rounded-3xl p-12 max-w-2xl mx-auto shadow-xl">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">No Content Available</h2>
              <p className="text-slate-600">
                This strategic profile doesn't contain any displayable content. Try regenerating the assessment results.
              </p>
              <button
                onClick={() => navigate('/college/assessment')}
                className="mt-4 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
              >
                Take Assessment
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default SavedStrategicProfile;